import React, { useState, useContext } from "react";
import {
  Box,
  Avatar,
  Typography,
  Stack,
  Chip,
  useTheme,
  ButtonBase,
  Collapse,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
} from "@mui/material";
import {
  LogOut,
  Moon,
  Sun,
  Timer,
  AlertTriangle,
  ChevronUp,
  UserCircle,
} from "lucide-react";
import { GB, FR, ES, PT, DE, NL, type FlagComponent } from "country-flag-icons/react/3x2";
import { useLogout, useGetLocale, useSetLocale, useTranslate } from "@refinedev/core";
import { useAuth } from "../../contexts/auth-context";
import { ColorModeContext } from "../../contexts/color-mode";
import { useNavigate } from "react-router-dom";
import { getTrialDaysRemaining, isTrialExpired } from "../../utils/trial-permissions";

// Liste des langues supportées (doit correspondre à I18n.ts)
const SUPPORTED_LANGUAGES = ["en", "fr", "es", "pt", "de", "nl"];

// Mapping langue -> composant drapeau
const flagComponents: Record<string, FlagComponent> = {
  en: GB,
  fr: FR,
  es: ES,
  pt: PT,
  de: DE,
  nl: NL,
};

// Mapping langue -> nom d'affichage (court)
// const languageNames: Record<string, string> = {
//   en: "EN",
//   fr: "FR",
//   es: "ES",
//   pt: "PT",
//   de: "DE",
//   nl: "NL",
// };

// Mapping langue -> nom d'affichage (complet)
const languageFullNames: Record<string, string> = {
  en: "English",
  fr: "Français",
  es: "Español",
  pt: "Português",
  de: "Deutsch",
  nl: "Nederlands",
};

interface SiderFooterProps {
  collapsed?: boolean;
}

export const SiderFooter: React.FC<SiderFooterProps> = ({ collapsed = false }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const { mutate: logout } = useLogout();
  const [open, setOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const translate = useTranslate();

  // Color mode context
  const { mode, setMode } = useContext(ColorModeContext);

  // Language context
  const changeLanguage = useSetLocale();
  const locale = useGetLocale();
  const currentLocale = locale();

  const handleClick = () => {
    setOpen(!open);
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    logout();
    setLogoutDialogOpen(false);
    setOpen(false);
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'error';
      case 'MODERATOR':
        return 'warning';
      case 'SUB_MODERATOR':
        return 'warning';
      case 'SAFEE_MODERATOR':
        return 'warning';
      case 'USER':
      default:
        return 'primary';
    }
  };

  if (!user) return null;

  // Trial info for MODERATOR_FREE users
  const daysRemaining = user.role === 'MODERATOR_FREE'
    ? getTrialDaysRemaining(user.freeTrialStartedAt || null)
    : 0;
  const isExpired = user.role === 'MODERATOR_FREE'
    ? isTrialExpired(user.freeTrialStartedAt || null)
    : false;

  const getTrialChipProps = () => {
    if (isExpired) {
      return {
        color: 'error' as const,
        icon: <AlertTriangle size={16} />,
        label: translate('trial.expired')
      };
    } else if (daysRemaining <= 3) {
      return {
        color: 'warning' as const,
        icon: <Timer size={16} />,
        label: translate('trial.daysRemainingShort', { days: daysRemaining })
      };
    } else {
      return {
        color: 'info' as const,
        icon: <Timer size={16} />,
        label: translate('trial.daysRemaining', { days: daysRemaining })
      };
    }
  };

  const menuItemSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    width: '100%',
    borderRadius: '12px',
    padding: '12px 16px',
    minHeight: '48px',
    justifyContent: 'flex-start',
    transition: 'all 0.2s ease',
  } as const;

  const menuItemHoverSx = {
    backgroundColor: theme.palette.mode === "dark"
      ? theme.palette.grey[800]
      : theme.palette.grey[200],
  } as const;

  return (
    <Box>
      {/* Trial Chip for MODERATOR_FREE users */}
      {user.role === 'MODERATOR_FREE' && (
        <Box sx={{
          p: collapsed ? 1 : 2,
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: theme.palette.mode === "dark"
            ? theme.palette.grey[900]
            : theme.palette.grey[50]
        }}>
          <Tooltip
            title={
              isExpired
                ? translate('trial.expiredTooltip')
                : translate('trial.remainingTooltip', { days: daysRemaining })
            }
            placement="right"
          >
            <Chip
              {...getTrialChipProps()}
              onClick={() => navigate('/pricing')}
              clickable
              sx={{
                width: '100%',
                fontWeight: 'bold',
                fontSize: collapsed ? '0.75rem' : '0.875rem',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: 2,
                },
                transition: 'all 0.3s ease',
                animation: daysRemaining <= 3 ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                  '100%': { transform: 'scale(1)' },
                },
              }}
            />
          </Tooltip>
        </Box>
      )}

      {/* User Profile Section - conteneur unifié */}
      <Box
        sx={{
          mx: 0,
          borderRadius: '12px',
          backgroundColor: open
            ? (theme.palette.mode === "dark"
              ? theme.palette.grey[900]
              : theme.palette.grey[50])
            : 'transparent',
          transition: 'background-color 0.2s ease',
          overflow: 'hidden',
        }}
      >
        <ButtonBase
          onClick={handleClick}
          sx={{
            width: "100%",
            pt: collapsed ? 0.5 : 1,
            px: collapsed ? 1 : 1.5,
            pb: collapsed ? 0.5 : 1,
            borderRadius: '12px',
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor: theme.palette.mode === "dark"
                ? theme.palette.grey[800]
                : theme.palette.grey[200],
            },
          }}
        >
          {collapsed ? (
            <Tooltip title={user.fullname || user.email} placement="right">
              <Stack alignItems="center" spacing={0.5}>
                <ChevronUp
                  size={14}
                  style={{
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                    color: theme.palette.text.secondary,
                  }}
                />
                <Avatar
                  src={user.profilePicture}
                  alt={user.fullname || user.email}
                  sx={{ width: 32, height: 32 }}
                />
              </Stack>
            </Tooltip>
          ) : (
            <Stack alignItems="center" sx={{ width: "100%" }}>
              <ChevronUp
                size={18}
                style={{
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  color: theme.palette.text.secondary,
                  marginBottom: 4,
                }}
              />
              <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%" }}>
                <Avatar
                  src={user.profilePicture}
                  alt={user.fullname || user.email}
                  sx={{ width: 40, height: 40 }}
                />
                <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      lineHeight: 1.2,
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {user.fullname || user.email}
                  </Typography>
                  {user.role && (
                    <Chip
                      label={user.role}
                      size="small"
                      color={getRoleColor(user.role)}
                      sx={{
                        height: 18,
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        mt: 0.5,
                      }}
                    />
                  )}
                </Box>
              </Stack>
            </Stack>
          )}
        </ButtonBase>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <Stack spacing={0.25} sx={{ px: 0.5, pb: 0.5 }}>
            {/* My Profile */}
            <ButtonBase
              onClick={() => {
                navigate('/profile');
                setOpen(false);
              }}
              sx={{
                ...menuItemSx,
                '&:hover': menuItemHoverSx,
              }}
            >
              <UserCircle size={18} />
              <Typography variant="body2">{translate('profile.title', 'Mon profil')}</Typography>
            </ButtonBase>

            {/* Dark Mode Toggle */}
            <ButtonBase
              onClick={() => setMode()}
              sx={{
                ...menuItemSx,
                '&:hover': menuItemHoverSx,
              }}
            >
              {mode === "dark" ? <Sun size={18} /> : <Moon size={18} />}
              <Typography variant="body2">
                {mode === "dark" ? translate('settings.lightMode', 'Mode clair') : translate('settings.darkMode', 'Mode sombre')}
              </Typography>
            </ButtonBase>

            {/* Language Selection */}
            <ButtonBase
              onClick={(e) => setLanguageMenuAnchor(e.currentTarget)}
              sx={{
                ...menuItemSx,
                '&:hover': menuItemHoverSx,
              }}
            >
              {(() => {
                const FlagComponent = flagComponents[currentLocale || 'en'];
                return FlagComponent ? (
                  <FlagComponent style={{ width: 22, height: 16, borderRadius: 2 }} />
                ) : (
                  <Box sx={{ width: 22, height: 16, backgroundColor: 'grey.300', borderRadius: 0.5 }} />
                );
              })()}
              <Typography variant="body2">{translate('settings.language', 'Langue')}</Typography>
            </ButtonBase>

            <Divider sx={{ mx: 0.5 }} />

            {/* Logout */}
            <ButtonBase
              onClick={handleLogoutClick}
              sx={{
                ...menuItemSx,
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.main',
                  color: 'error.contrastText',
                },
              }}
            >
              <LogOut size={18} />
              <Typography variant="body2">{translate('sidebar.logout')}</Typography>
            </ButtonBase>
          </Stack>

        {/* Language Menu */}
        <Menu
          anchorEl={languageMenuAnchor}
          open={Boolean(languageMenuAnchor)}
          onClose={() => setLanguageMenuAnchor(null)}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        >
          {SUPPORTED_LANGUAGES.map((lang: string) => {
            const FlagComponent = flagComponents[lang];
            return (
              <MenuItem
                key={lang}
                selected={lang === currentLocale}
                onClick={() => {
                  changeLanguage(lang);
                  setLanguageMenuAnchor(null);
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  {FlagComponent ? (
                    <FlagComponent style={{ width: 22, height: 16, borderRadius: 2 }} />
                  ) : (
                    <Box sx={{ width: 22, height: 16, backgroundColor: 'grey.300', borderRadius: 0.5 }} />
                  )}
                  <Typography variant="body2">
                    {languageFullNames[lang] || lang.toUpperCase()}
                  </Typography>
                </Stack>
              </MenuItem>
            );
          })}
        </Menu>
        </Collapse>
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">
          {translate('sidebar.logout.confirm.title', 'Déconnexion')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            {translate('sidebar.logout.confirm.message', 'Êtes-vous sûr de vouloir vous déconnecter ?')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="inherit">
            {translate('common.cancel', 'Annuler')}
          </Button>
          <Button onClick={handleLogoutConfirm} color="error" variant="contained" autoFocus>
            {translate('sidebar.logout', 'Déconnexion')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
